import * as React from "react"
import { Head, router, usePage } from "@inertiajs/react"
import { Copy, Eye, EyeOff, RefreshCw } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import type { PageProps, ApiSetting } from "@/types/inertia"

type Props = PageProps & {
  api_setting: ApiSetting
  webhook_url: string
}

const SCHEDULED_JOBS = [
  {
    key: "seo_audit" as const,
    name: "SEO Audit",
    cron: "0 9 */14 * *",
    description: "Bi-weekly",
    enabledKey: "seo_audit_enabled" as keyof ApiSetting,
  },
  {
    key: "sitemap" as const,
    name: "Sitemap Rebuild",
    cron: "0 2 * * 0",
    description: "Weekly Sunday",
    enabledKey: "sitemap_enabled" as keyof ApiSetting,
  },
  {
    key: "analytics" as const,
    name: "Analytics Snapshot",
    cron: "0 0 1 * *",
    description: "Monthly",
    enabledKey: "analytics_enabled" as keyof ApiSetting,
  },
]

const PERMISSIONS = [
  { label: "Read pages",      allowed: true },
  { label: "Write meta",      allowed: true },
  { label: "Rebuild sitemap", allowed: true },
  { label: "Delete content",  allowed: false },
  { label: "Publish articles", allowed: false },
]

function CopyButton({ value, label = "Copy" }: { value: string; label?: string }) {
  const [copied, setCopied] = React.useState(false)

  function handleCopy() {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs text-ink-muted hover:bg-stone-100 transition-colors"
    >
      <Copy size={13} />
      {copied ? "Copied!" : label}
    </button>
  )
}

function ToggleSwitch({ enabled, onChange }: { enabled: boolean; onChange: () => void }) {
  return (
    <button
      onClick={onChange}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
        enabled ? "bg-teal" : "bg-stone-300"
      }`}
      aria-checked={enabled}
      role="switch"
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
          enabled ? "translate-x-5" : "translate-x-1"
        }`}
      />
    </button>
  )
}

export default function AdminApi() {
  const { props } = usePage<Props>()
  const { api_setting, webhook_url, flash } = props

  const [revealed, setRevealed] = React.useState(false)

  function handleRegenerate() {
    if (!confirm("Regenerate the API key? The current key will stop working immediately.")) return
    router.post("/admin/api/regenerate-key")
  }

  function handleToggleJob(jobKey: string) {
    router.patch("/admin/api/toggle-job", { job: jobKey })
  }

  const displayKey = revealed ? api_setting.api_key_full : api_setting.api_key_masked

  return (
    <AdminShell>
      <Head title="API">
        <meta name="description" content="API key management for the Sanctuary SEO agent." />
        <meta property="og:title" content="API" />
        <meta property="og:description" content="API key management for the Sanctuary SEO agent." />
      </Head>
      <PageHeader title="API" description="API key and SEO agent configuration." />

      {flash.notice && (
        <div className="mt-4 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
          {flash.notice}
        </div>
      )}

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        {/* Left column */}
        <div className="space-y-6">
          {/* API key card */}
          <div className="rounded-lg border border-hairline bg-surface p-5">
            <h2 className="mb-3">API Key</h2>
            <div className="flex items-center gap-2 rounded border border-hairline bg-page px-3 py-2 font-mono text-sm text-ink-body overflow-hidden">
              <span className="flex-1 truncate">{displayKey}</span>
              <button
                onClick={() => setRevealed(!revealed)}
                className="shrink-0 text-ink-muted hover:text-ink-body transition-colors"
                title={revealed ? "Hide key" : "Reveal key"}
              >
                {revealed ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            <div className="mt-3 flex gap-2">
              <CopyButton value={api_setting.api_key_full} label="Copy key" />
              <button
                onClick={handleRegenerate}
                className="inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs text-red-500 hover:bg-red-50 transition-colors"
              >
                <RefreshCw size={13} />
                Regenerate
              </button>
            </div>
            <p className="mt-3 text-xs text-ink-muted">
              Include as <code className="rounded bg-stone-100 px-1">Authorization: Bearer &lt;key&gt;</code> in all API requests.
            </p>
          </div>

          {/* Webhook URL */}
          <div className="rounded-lg border border-hairline bg-surface p-5">
            <h2 className="mb-3">Webhook Endpoint</h2>
            <div className="flex items-center gap-2 rounded border border-hairline bg-page px-3 py-2 font-mono text-sm text-ink-body overflow-hidden">
              <span className="flex-1 truncate">{webhook_url}</span>
            </div>
            <div className="mt-2">
              <CopyButton value={webhook_url} label="Copy URL" />
            </div>
            <p className="mt-2 text-xs text-ink-muted">
              POST an audit payload here after each agent run. Same Bearer token authentication.
            </p>
          </div>

          {/* Agent permissions */}
          <div className="rounded-lg border border-hairline bg-surface p-5">
            <h2 className="mb-3">Agent Permissions</h2>
            <ul className="space-y-2">
              {PERMISSIONS.map((perm) => (
                <li key={perm.label} className="flex items-center justify-between text-sm">
                  <span className="text-ink-body">{perm.label}</span>
                  <span
                    className={`font-medium ${perm.allowed ? "text-green-600" : "text-ink-muted line-through"}`}
                  >
                    {perm.allowed ? "✓" : "✗"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Scheduled jobs */}
          <div className="rounded-lg border border-hairline bg-surface p-5">
            <h2 className="mb-3">Scheduled Jobs</h2>
            <ul className="divide-y divide-hairline">
              {SCHEDULED_JOBS.map((job) => {
                const enabled = Boolean(api_setting[job.enabledKey])
                return (
                  <li key={job.key} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                    <div>
                      <p className="text-sm font-medium text-ink-body">{job.name}</p>
                      <p className="text-xs text-ink-muted">
                        <code className="mr-1">{job.cron}</code>· {job.description}
                      </p>
                    </div>
                    <ToggleSwitch
                      enabled={enabled}
                      onChange={() => handleToggleJob(job.key)}
                    />
                  </li>
                )
              })}
            </ul>
          </div>

          {/* Connected agents */}
          <div className="rounded-lg border border-hairline bg-surface p-5">
            <h2 className="mb-3">Connected Agents</h2>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span
                  className={`h-2 w-2 rounded-full ${
                    api_setting.last_agent_connected_at ? "bg-green-500" : "bg-stone-300"
                  }`}
                />
                <span className="text-sm text-ink-body">SEO Agent</span>
              </div>
              <span className="text-xs text-ink-muted">
                {api_setting.last_agent_connected_at
                  ? `Last seen ${new Date(api_setting.last_agent_connected_at).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}`
                  : "Never connected"}
              </span>
            </div>
          </div>

          {/* API reference */}
          <div className="rounded-lg border border-hairline bg-surface p-5">
            <h2 className="mb-3">API Endpoints</h2>
            <ul className="space-y-2 text-xs font-mono text-ink-muted">
              <li><span className="text-green-600">GET</span>  /api/v1/pages</li>
              <li><span className="text-amber-600">PATCH</span> /api/v1/pages/:slug/seo</li>
              <li><span className="text-green-600">GET</span>  /api/v1/therapists</li>
              <li><span className="text-blue-600">POST</span> /api/v1/sitemap/rebuild</li>
              <li><span className="text-blue-600">POST</span> /api/v1/audit/report</li>
            </ul>
          </div>
        </div>
      </div>
    </AdminShell>
  )
}
